import React, { useState, useEffect } from 'react';
import { Dialog, Transition, Tab } from '@headlessui/react';
import { Fragment } from 'react';
import {
  QuestionMarkCircleIcon,
  XMarkIcon,
  BookOpenIcon,
  PlayIcon,
  ChatBubbleLeftRightIcon,
  CogIcon,
  LightBulbIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';

interface HelpSystemProps {
  isOpen: boolean;
  onClose: () => void;
  context?: string; // Current page/feature context
}

interface HelpTopic {
  id: string;
  title: string;
  description: string;
  category: 'getting-started' | 'features' | 'troubleshooting' | 'advanced';
  icon: React.ElementType;
  content: React.ReactNode;
  videoUrl?: string;
  estimatedTime?: string;
}

const HelpSystem: React.FC<HelpSystemProps> = ({ isOpen, onClose, context }) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredTopics, setFilteredTopics] = useState<HelpTopic[]>([]);

  const helpTopics: HelpTopic[] = [
    // Getting Started
    {
      id: 'quick-start',
      title: 'Quick Start Guide',
      description: 'Get up and running in 5 minutes',
      category: 'getting-started',
      icon: RocketLaunchIcon,
      estimatedTime: '5 min',
      videoUrl: 'https://youtu.be/quickstart',
      content: (
        <div className="space-y-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">🎯 Welcome to AI-Accelo Mirror!</h4>
            <p className="text-blue-800">The world's most advanced PSA platform. Let's get you started quickly.</p>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <CheckCircleIcon className="w-5 h-5 text-green-500 mt-0.5" />
              <div>
                <h5 className="font-medium">1. Set up your organization</h5>
                <p className="text-sm text-gray-600">Go to Settings → Organization to configure your company details.</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <CheckCircleIcon className="w-5 h-5 text-green-500 mt-0.5" />
              <div>
                <h5 className="font-medium">2. Add team members</h5>
                <p className="text-sm text-gray-600">Invite your team via Settings → Users. Assign appropriate roles.</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <CheckCircleIcon className="w-5 h-5 text-green-500 mt-0.5" />
              <div>
                <h5 className="font-medium">3. Create your first client</h5>
                <p className="text-sm text-gray-600">Navigate to Clients → Add Client to start managing relationships.</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <CheckCircleIcon className="w-5 h-5 text-green-500 mt-0.5" />
              <div>
                <h5 className="font-medium">4. Set up integrations</h5>
                <p className="text-sm text-gray-600">Connect Xero, Slack, Stripe, and Gmail for maximum automation.</p>
              </div>
            </div>
          </div>
          
          <div className="bg-yellow-50 p-4 rounded-lg">
            <div className="flex items-center space-x-2">
              <LightBulbIcon className="w-5 h-5 text-yellow-600" />
              <h5 className="font-medium text-yellow-900">Pro Tip</h5>
            </div>
            <p className="text-sm text-yellow-800 mt-1">
              Enable AI features with your OpenAI API key for intelligent automation throughout the platform.
            </p>
          </div>
        </div>
      )
    },
    
    {
      id: 'ai-features',
      title: 'AI-Powered Features',
      description: 'Leverage artificial intelligence for automation',
      category: 'features',
      icon: LightBulbIcon,
      estimatedTime: '10 min',
      videoUrl: 'https://youtu.be/ai-features',
      content: (
        <div className="space-y-4">
          <div className="bg-purple-50 p-4 rounded-lg">
            <h4 className="font-semibold text-purple-900 mb-2">🤖 AI Throughout Your Platform</h4>
            <p className="text-purple-800">Our AI integration provides intelligent automation for every aspect of your business.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white p-4 border rounded-lg">
              <h5 className="font-medium mb-2">🎫 Smart Ticket Analysis</h5>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Auto-categorization</li>
                <li>• Priority assignment</li>
                <li>• Sentiment analysis</li>
                <li>• Resolution time estimation</li>
              </ul>
            </div>
            
            <div className="bg-white p-4 border rounded-lg">
              <h5 className="font-medium mb-2">💼 Contract Intelligence</h5>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Risk assessment</li>
                <li>• Profitability prediction</li>
                <li>• Optimization suggestions</li>
                <li>• Renewal likelihood</li>
              </ul>
            </div>
            
            <div className="bg-white p-4 border rounded-lg">
              <h5 className="font-medium mb-2">👥 Client Insights</h5>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Churn prediction</li>
                <li>• Satisfaction scoring</li>
                <li>• Upsell opportunities</li>
                <li>• Communication preferences</li>
              </ul>
            </div>
            
            <div className="bg-white p-4 border rounded-lg">
              <h5 className="font-medium mb-2">💰 Financial Forecasting</h5>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Revenue predictions</li>
                <li>• Cash flow analysis</li>
                <li>• Risk factor identification</li>
                <li>• Growth recommendations</li>
              </ul>
            </div>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg">
            <h5 className="font-medium text-green-900 mb-2">🚀 Getting Started with AI</h5>
            <ol className="text-sm text-green-800 space-y-1">
              <li>1. Add your OpenAI API key in Settings → Integrations</li>
              <li>2. AI features will automatically activate across the platform</li>
              <li>3. Look for the ✨ AI badges on tickets, contracts, and clients</li>
              <li>4. Use the natural language query box to ask questions about your data</li>
            </ol>
          </div>
        </div>
      )
    },
    
    {
      id: 'contract-management',
      title: 'Contract & Retainer Management',
      description: 'Master the industry-leading contract system',
      category: 'features',
      icon: BookOpenIcon,
      estimatedTime: '15 min',
      content: (
        <div className="space-y-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">💼 Industry-Leading Contract Management</h4>
            <p className="text-blue-800">Our contract system exceeds Accelo and HaloPSA with advanced automation and intelligence.</p>
          </div>
          
          <div className="space-y-4">
            <div>
              <h5 className="font-medium mb-2">📋 6 Contract Types Supported</h5>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="bg-gray-50 p-2 rounded">• Retainer</div>
                <div className="bg-gray-50 p-2 rounded">• Fixed Price</div>
                <div className="bg-gray-50 p-2 rounded">• Time & Materials</div>
                <div className="bg-gray-50 p-2 rounded">• Recurring Service</div>
                <div className="bg-gray-50 p-2 rounded">• Maintenance</div>
                <div className="bg-gray-50 p-2 rounded">• Support</div>
              </div>
            </div>
            
            <div>
              <h5 className="font-medium mb-2">⚡ Automated Features</h5>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Automatic contract numbering</li>
                <li>• Smart period creation for retainers</li>
                <li>• Rollover hour calculation with limits</li>
                <li>• Overage rate handling</li>
                <li>• Renewal notifications</li>
                <li>• AI-powered risk assessment</li>
              </ul>
            </div>
            
            <div>
              <h5 className="font-medium mb-2">🔄 Contract Lifecycle</h5>
              <div className="flex items-center space-x-2 text-sm">
                <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded">Draft</span>
                <span>→</span>
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">Pending</span>
                <span>→</span>
                <span className="bg-green-100 text-green-800 px-2 py-1 rounded">Active</span>
                <span>→</span>
                <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">Renewal</span>
              </div>
            </div>
          </div>
          
          <div className="bg-indigo-50 p-4 rounded-lg">
            <h5 className="font-medium text-indigo-900 mb-2">💡 Best Practices</h5>
            <ul className="text-sm text-indigo-800 space-y-1">
              <li>• Use contract templates for consistency</li>
              <li>• Set up rate cards for accurate billing</li>
              <li>• Monitor retainer consumption regularly</li>
              <li>• Enable AI analysis for optimization insights</li>
              <li>• Set renewal reminders 30-60 days in advance</li>
            </ul>
          </div>
        </div>
      )
    },
    
    {
      id: 'integrations-setup',
      title: 'Setting Up Integrations',
      description: 'Connect with Xero, Slack, Stripe, and Gmail',
      category: 'getting-started',
      icon: CogIcon,
      estimatedTime: '20 min',
      content: (
        <div className="space-y-4">
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-semibold text-green-900 mb-2">🔗 Enterprise Integrations</h4>
            <p className="text-green-800">Connect your favorite tools for seamless workflow automation.</p>
          </div>
          
          <div className="space-y-6">
            <div className="border rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <span className="text-blue-600 font-bold text-sm">X</span>
                </div>
                <div>
                  <h5 className="font-medium">Xero Accounting</h5>
                  <p className="text-sm text-gray-600">Bi-directional sync of contacts, invoices, and payments</p>
                </div>
              </div>
              <div className="text-sm text-gray-600 space-y-1">
                <p><strong>Setup:</strong> Create app at developer.xero.com</p>
                <p><strong>Scopes needed:</strong> accounting.contacts, accounting.transactions</p>
                <p><strong>Features:</strong> Auto-sync clients, push invoices, sync payments</p>
              </div>
            </div>
            
            <div className="border rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <span className="text-purple-600 font-bold text-sm">S</span>
                </div>
                <div>
                  <h5 className="font-medium">Slack</h5>
                  <p className="text-sm text-gray-600">Rich notifications and interactive commands</p>
                </div>
              </div>
              <div className="text-sm text-gray-600 space-y-1">
                <p><strong>Setup:</strong> Create app at api.slack.com/apps</p>
                <p><strong>Features:</strong> Ticket alerts, SLA notifications, bot commands</p>
                <p><strong>Channels:</strong> Auto-route notifications by type</p>
              </div>
            </div>
            
            <div className="border rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <span className="text-indigo-600 font-bold text-sm">S</span>
                </div>
                <div>
                  <h5 className="font-medium">Stripe</h5>
                  <p className="text-sm text-gray-600">Secure payment processing and subscriptions</p>
                </div>
              </div>
              <div className="text-sm text-gray-600 space-y-1">
                <p><strong>Setup:</strong> Get API keys from stripe.com dashboard</p>
                <p><strong>Features:</strong> Invoice payments, subscription billing, webhooks</p>
                <p><strong>Security:</strong> PCI-compliant payment flows</p>
              </div>
            </div>
            
            <div className="border rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                  <span className="text-red-600 font-bold text-sm">G</span>
                </div>
                <div>
                  <h5 className="font-medium">Gmail</h5>
                  <p className="text-sm text-gray-600">Automated email campaigns and notifications</p>
                </div>
              </div>
              <div className="text-sm text-gray-600 space-y-1">
                <p><strong>Setup:</strong> Enable Gmail API in Google Cloud Console</p>
                <p><strong>Features:</strong> Invoice delivery, contract emails, ticket notifications</p>
                <p><strong>Templates:</strong> Customizable email templates with variables</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    
    {
      id: 'troubleshooting',
      title: 'Common Issues & Solutions',
      description: 'Quick fixes for typical problems',
      category: 'troubleshooting',
      icon: ExclamationTriangleIcon,
      estimatedTime: '5 min',
      content: (
        <div className="space-y-4">
          <div className="bg-yellow-50 p-4 rounded-lg">
            <h4 className="font-semibold text-yellow-900 mb-2">🔧 Troubleshooting Guide</h4>
            <p className="text-yellow-800">Quick solutions for the most common issues.</p>
          </div>
          
          <div className="space-y-4">
            <div className="border border-red-200 rounded-lg p-4">
              <h5 className="font-medium text-red-900 mb-2">❌ "AI features not working"</h5>
              <div className="text-sm text-gray-700 space-y-1">
                <p><strong>Cause:</strong> OpenAI API key not configured or invalid</p>
                <p><strong>Solution:</strong></p>
                <ul className="ml-4 space-y-1">
                  <li>• Check API key starts with 'sk-'</li>
                  <li>• Verify account has credits</li>
                  <li>• Test with a simple API call</li>
                  <li>• Check Settings → Integrations → AI</li>
                </ul>
              </div>
            </div>
            
            <div className="border border-red-200 rounded-lg p-4">
              <h5 className="font-medium text-red-900 mb-2">❌ "Xero sync failing"</h5>
              <div className="text-sm text-gray-700 space-y-1">
                <p><strong>Cause:</strong> Token expired or scope issues</p>
                <p><strong>Solution:</strong></p>
                <ul className="ml-4 space-y-1">
                  <li>• Reconnect Xero in Settings → Integrations</li>
                  <li>• Check tenant connection is active</li>
                  <li>• Verify redirect URI matches exactly</li>
                  <li>• Review app scopes in Xero developer portal</li>
                </ul>
              </div>
            </div>
            
            <div className="border border-red-200 rounded-lg p-4">
              <h5 className="font-medium text-red-900 mb-2">❌ "Slow performance"</h5>
              <div className="text-sm text-gray-700 space-y-1">
                <p><strong>Cause:</strong> Large datasets or missing cache</p>
                <p><strong>Solution:</strong></p>
                <ul className="ml-4 space-y-1">
                  <li>• Enable Redis caching if available</li>
                  <li>• Use filters to limit data queries</li>
                  <li>• Archive old records regularly</li>
                  <li>• Check browser network tab for slow requests</li>
                </ul>
              </div>
            </div>
            
            <div className="border border-red-200 rounded-lg p-4">
              <h5 className="font-medium text-red-900 mb-2">❌ "Stripe payments not working"</h5>
              <div className="text-sm text-gray-700 space-y-1">
                <p><strong>Cause:</strong> Webhook or API key issues</p>
                <p><strong>Solution:</strong></p>
                <ul className="ml-4 space-y-1">
                  <li>• Verify webhook endpoint is configured</li>
                  <li>• Check API keys are for correct environment</li>
                  <li>• Test with Stripe CLI: stripe listen</li>
                  <li>• Review webhook delivery logs in Stripe dashboard</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="bg-blue-50 p-4 rounded-lg">
            <h5 className="font-medium text-blue-900 mb-2">📞 Still Need Help?</h5>
            <div className="text-sm text-blue-800 space-y-1">
              <p>• Check our <strong>complete documentation</strong> at docs.ai-accelo-mirror.com</p>
              <p>• Join our <strong>Discord community</strong> for real-time help</p>
              <p>• Email <strong>support@ai-accelo-mirror.com</strong> for technical issues</p>
              <p>• Browse <strong>GitHub issues</strong> for known problems and solutions</p>
            </div>
          </div>
        </div>
      )
    }
  ];

  const categories = [
    { id: 'getting-started', name: 'Getting Started', icon: RocketLaunchIcon },
    { id: 'features', name: 'Features', icon: LightBulbIcon },
    { id: 'troubleshooting', name: 'Troubleshooting', icon: ExclamationTriangleIcon },
    { id: 'advanced', name: 'Advanced', icon: CogIcon }
  ];

  useEffect(() => {
    const filtered = helpTopics.filter(topic => {
      const matchesSearch = topic.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           topic.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesTab = selectedTab === 0 || topic.category === categories[selectedTab - 1]?.id;
      return matchesSearch && matchesTab;
    });
    setFilteredTopics(filtered);
  }, [searchQuery, selectedTab]);

  const contextualHelp = () => {
    if (!context) return null;
    
    const contextMap: Record<string, string> = {
      'dashboard': 'quick-start',
      'tickets': 'ai-features',
      'contracts': 'contract-management',
      'settings': 'integrations-setup'
    };
    
    const suggestedTopic = helpTopics.find(t => t.id === contextMap[context]);
    
    if (suggestedTopic) {
      return (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-2 mb-2">
            <LightBulbIcon className="w-5 h-5 text-blue-600" />
            <h4 className="font-medium text-blue-900">Suggested for this page</h4>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h5 className="font-medium text-blue-900">{suggestedTopic.title}</h5>
              <p className="text-sm text-blue-700">{suggestedTopic.description}</p>
            </div>
            <button
              onClick={() => {
                // Would scroll to or highlight the topic
              }}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              View Guide →
            </button>
          </div>
        </div>
      );
    }
    
    return null;
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-6xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
                <div className="flex h-[80vh]">
                  {/* Sidebar */}
                  <div className="w-80 border-r bg-gray-50 p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-lg font-semibold text-gray-900">Help Center</h3>
                      <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <XMarkIcon className="w-6 h-6" />
                      </button>
                    </div>

                    {/* Search */}
                    <div className="mb-6">
                      <input
                        type="text"
                        placeholder="Search help topics..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    {/* Category Tabs */}
                    <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
                      <Tab.List className="flex flex-col space-y-1 mb-4">
                        <Tab className={({ selected }) =>
                          `w-full text-left px-3 py-2 rounded-md text-sm font-medium ${
                            selected
                              ? 'bg-blue-100 text-blue-700'
                              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                          }`
                        }>
                          All Topics
                        </Tab>
                        {categories.map((category) => (
                          <Tab
                            key={category.id}
                            className={({ selected }) =>
                              `w-full text-left px-3 py-2 rounded-md text-sm font-medium flex items-center space-x-2 ${
                                selected
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                              }`
                            }
                          >
                            <category.icon className="w-4 h-4" />
                            <span>{category.name}</span>
                          </Tab>
                        ))}
                      </Tab.List>
                    </Tab.Group>

                    {/* Topic List */}
                    <div className="space-y-2">
                      {filteredTopics.map((topic) => (
                        <button
                          key={topic.id}
                          className="w-full text-left p-3 rounded-lg border hover:bg-gray-50 transition-colors"
                          onClick={() => {
                            // Handle topic selection
                          }}
                        >
                          <div className="flex items-start space-x-3">
                            <topic.icon className="w-5 h-5 text-gray-400 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <h4 className="text-sm font-medium text-gray-900 truncate">
                                {topic.title}
                              </h4>
                              <p className="text-xs text-gray-500 mt-1">
                                {topic.description}
                              </p>
                              {topic.estimatedTime && (
                                <span className="inline-block text-xs text-blue-600 mt-1">
                                  {topic.estimatedTime}
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Main Content */}
                  <div className="flex-1 p-8 overflow-y-auto">
                    {contextualHelp()}
                    
                    {filteredTopics.length > 0 ? (
                      <div className="space-y-8">
                        {filteredTopics.map((topic) => (
                          <div key={topic.id} className="border-b pb-8 last:border-b-0">
                            <div className="flex items-center space-x-3 mb-4">
                              <topic.icon className="w-6 h-6 text-blue-600" />
                              <div>
                                <h2 className="text-xl font-semibold text-gray-900">
                                  {topic.title}
                                </h2>
                                <p className="text-gray-600">{topic.description}</p>
                              </div>
                              {topic.videoUrl && (
                                <a
                                  href={topic.videoUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center space-x-1 text-blue-600 hover:text-blue-700 text-sm"
                                >
                                  <PlayIcon className="w-4 h-4" />
                                  <span>Watch Video</span>
                                </a>
                              )}
                            </div>
                            
                            <div className="prose max-w-none">
                              {topic.content}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <QuestionMarkCircleIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                          No help topics found
                        </h3>
                        <p className="text-gray-600">
                          Try adjusting your search or browse different categories.
                        </p>
                      </div>
                    )}

                    {/* Contact Support */}
                    <div className="mt-12 bg-gray-50 rounded-lg p-6">
                      <div className="flex items-center space-x-3 mb-4">
                        <ChatBubbleLeftRightIcon className="w-6 h-6 text-blue-600" />
                        <h3 className="text-lg font-semibold text-gray-900">
                          Still need help?
                        </h3>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <a
                          href="https://discord.gg/ai-accelo-mirror"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-3 p-4 bg-white rounded-lg border hover:shadow-md transition-shadow"
                        >
                          <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                            <ChatBubbleLeftRightIcon className="w-5 h-5 text-indigo-600" />
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900">Discord Community</h4>
                            <p className="text-sm text-gray-600">Chat with other users</p>
                          </div>
                        </a>
                        
                        <a
                          href="mailto:support@ai-accelo-mirror.com"
                          className="flex items-center space-x-3 p-4 bg-white rounded-lg border hover:shadow-md transition-shadow"
                        >
                          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                            <ChatBubbleLeftRightIcon className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900">Email Support</h4>
                            <p className="text-sm text-gray-600">Get direct help</p>
                          </div>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default HelpSystem;